import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MapMarker from "mdi-react/MapMarkerIcon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Accordion from "@mui/material/Accordion";
import { AvField } from "availity-reactstrap-validation";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

import { ReactComponent as ClockSvg } from "../../../../assets/icons/clock.svg";
import { ReactComponent as DeleteSvg } from "../../../../assets/icons/delete.svg";
import { ReactComponent as MapMarkerSvg } from "../../../../assets/icons/map-marker.svg";
import { ReactComponent as ChevronDownSvg } from "../../../../assets/icons/chevron-down.svg";
import { ReactComponent as DateCalenderSvg } from "../../../../assets/icons/date-calender.svg";
import {
  messages,
  fieldName,
  EMPTY_STRING,
  durationOptions,
  sharedConstants,
  eventBookingStatus,
  DATE_PICKER_FORMAT,
} from "../../../../constants";
import {
  isValidDate,
  getFutureDate,
  filterByStatus,
  combineDateAndTime,
  isDateBeforeTomorrow,
} from "../../../../shared";
import CustomTooltip from "../../../../shared/components/ToolTip/CustomTooltip";
import InputGroupWrapper from "../../../../shared/components/InputGroup/InputGroupWrapper";
import { eventBookingActions } from "../../../../redux/actions/eventBooking.actions";

const { EVENT_NAME } = sharedConstants;

const customInputStyle = {
  display: "block",
  height: "calc(1.5em + 0.75rem + 2px)",
  padding: "0.375rem 0.75rem",
  fontSize: "1rem",
  fontWeight: 400,
  lineHeight: 1.5,
  color: "#495057",
  backgroundColor: "#fff",
  backgroundClip: "padding-box",
  border: "1px solid #ced4da",
  borderRight: "none",
};

const EventSetup = (props) => {
  const { focusErrorFieldRef, emptyStates, handleDeleteEvent } = props;

  const { t } = useTranslation("common");

  const inputRef = useRef(null);
  const [address, setAddress] = useState(EMPTY_STRING);
  const [startDate, setStartDate] = useState();

  useEffect(() => {
    if (props.event.eventDate) {
      setStartDate(new Date(props.event.eventDate));
    } else {
      setStartDate(null);
    }
    // eslint-disable-next-line
  }, [props.event.eventDate]);

  // eslint-disable-next-line
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);

  const getPostalCode = (place) => {
    // Check if the place has address components
    if (place && place.address_components) {
      // Iterate through address components to find the postal code
      for (const component of place.address_components) {
        if (component.types.includes("postal_code")) {
          return component.long_name; // This is the postal code
        }
      }
    }
    return null; // Return null if no postal code found
  };

  const handleSelect = async (selectedAddress) => {
    setAddress(selectedAddress);
    try {
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);
      const postalCode = getPostalCode(results[0]);

      const components = results?.[0]?.address_components || [];
      const componentMap = components.reduce((acc, comp) => {
        comp.types.forEach((type) => {
          acc[type] = {
            long: comp.long_name,
            short: comp.short_name,
          };
        });
        return acc;
      }, {});

      const updatedModel = props.formState.map((event) =>
        event.id === props.event.id
          ? {
              ...event,
              eventLocation: {
                ...event?.eventLocation,
                currentLocation: selectedAddress,
                latitude: latLng.lat,
                longitude: latLng.lng,
                postalCode: postalCode,
                stateOrProvince:
                  componentMap.administrative_area_level_1?.short ||
                  EMPTY_STRING,
                city: componentMap.locality?.long || EMPTY_STRING,
                country: componentMap.country?.long || EMPTY_STRING,
                countryCode: componentMap.country?.short || EMPTY_STRING,
              },
            }
          : event
      );

      props.setFormState(updatedModel);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    props.loginAsAdmin(props.isAuthenticated);
  }, []);

  useEffect(() => {
    if (address) {
      const isFromSuggestions = autocompleteSuggestions?.some(
        (s) => s.description === address
      );

      if (isFromSuggestions) {
        handleSelect(address);
      } else {
        const updatedModel = props.formState.map((event) =>
          event.id === props.event.id
            ? {
                ...event,
                eventLocation: {
                  ...event?.eventLocation,
                  currentLocation: address,
                  latitude: null,
                  longitude: null,
                  postalCode: EMPTY_STRING,
                },
              }
            : event
        );
        props.setFormState(updatedModel);
      }
    }
    // eslint-disable-next-line
  }, [address]);

  const validatePostalCodes = () => {
    if (!props.event?.eventLocation?.postalCode) {
      return "enter precise location";
    }
    if (
      props.event.eventLocation.postalCode &&
      props.event.eventLocation.postalCode !== EMPTY_STRING
    ) {
      const first3Chars = props.event.eventLocation.postalCode
        .slice(0, 3)
        .toLowerCase();
      //Check if the first 3 characters of postalCode
      // match any item in the postalCodesList
      if (
        props.postalCodesList &&
        props.postalCodesList.some(
          (code) => code.slice(0, 3).toLowerCase() === first3Chars
        )
      ) {
        return true;
      }

      // If not, check if the first 2 characters of postalCode
      // match any item in postalCodesList where the third character is "*"
      const first2Chars = props.event.eventLocation.postalCode
        .slice(0, 2)
        .toLowerCase();
      if (
        props.postalCodesList &&
        props.postalCodesList.some(
          (code) =>
            code.slice(0, 2).toLowerCase() === first2Chars &&
            code.charAt(2) === "*"
        )
      ) {
        return true;
      }
    }
    // If neither condition is met, return an error message
    return "we are not servicing this area, please visit our contact us page to join our mailing list";
  };

  const handleDateChange = (date) => {
    const isEmpty = !date;

    // date must be >= tomorrow
    const isInvalid = isDateBeforeTomorrow(date);
    setStartDate(date);

    // Get existing time
    if (date && !isInvalid) {
      const eventStartTime = props.event.eventStartTime
        ? new Date(props.event.eventStartTime)
        : EMPTY_STRING;

      const sbStartTime = props.event.sbStartTime
        ? new Date(props.event.sbStartTime)
        : EMPTY_STRING;

      const eventStartDateTime = combineDateAndTime(date, eventStartTime);
      const sbStartDateTime = combineDateAndTime(date, sbStartTime);

      updateEventField({
        [fieldName.EVENT_DATE]: date.toLocaleDateString("en-US"),
        [fieldName.EVENT_START_TIME]: eventStartDateTime,
        [fieldName.SHUTTERB_START_TIME]: sbStartDateTime,
      });
    } else {
      updateEventField({
        [fieldName.EVENT_DATE]: date
          ? date.toLocaleDateString("en-US")
          : EMPTY_STRING,
      });
    }

    // Update error states
    props.setEmptyStates({
      eventDate: isEmpty,
      eventDateInvalid: isInvalid,
    });
  };

  const handleRawChange = (e) => {
    const value = e.target.value;
    if (isValidDate(value)) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) {
        handleDateChange(parsed);
      }
    } else {
      setStartDate(null);
      updateEventField({ [fieldName.EVENT_DATE]: EMPTY_STRING });
    }
  };

  const updateEventField = (updates) => {
    const updatedModel = props.formState.map((event) =>
      event.id === props.event.id ? { ...event, ...updates } : event
    );
    props.setFormState(updatedModel);
    props.setEventAlreadyExist(!props.isEditEvent);
  };

  return (
    <Accordion
      key={props.index}
      defaultExpanded
      className="event-setup-accordion"
    >
      <AccordionSummary
        className="event-setup-summary"
        expandIcon={<ChevronDownSvg />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <div className="form-label label-content-500">
          Event {props.index + 1} Details:
          {filterByStatus(props.formState, [eventBookingStatus.CANCEL]).length >
            1 && (
            <button
              type="button"
              className="icon-button ml-1"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteEvent(props.event);
              }}
            >
              <DeleteSvg />
            </button>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div id="form-content">
          <div className="row">
            <div className="col-md-6 pr-0">
              <label
                className="form-label label-content-500"
                ref={(element) => {
                  if (element) {
                    if (
                      !Array.isArray(focusErrorFieldRef.current[props.event.id])
                    ) {
                      focusErrorFieldRef.current[props.event.id] = [];
                    }
                    focusErrorFieldRef.current[props.event.id].push(element);
                  }
                }}
                htmlFor="eventName"
              >
                {t("event_name")}
              </label>

              <InputGroupWrapper>
                <AvField
                  name={`event[${props.event.id}].eventName`}
                  type="text"
                  className="form-control pr-4"
                  value={props.event.eventName}
                  maxLength="70"
                  validate={{
                    required: {
                      value: true,
                      errorMessage: t("please_enter_an_event_name"),
                    },
                  }}
                  onChange={(event, value) =>
                    updateEventField({ [EVENT_NAME]: value })
                  }
                />
              </InputGroupWrapper>
            </div>

            <div className="col-md-3 pr-0">
              <label
                className="form-label label-content-500"
                htmlFor={fieldName.EVENT_DATE}
              >
                {t("event_date")}
              </label>
              <div className="position-relative datepicker-wrapper mb-3">
                <DatePicker
                  key={`datepicker_${props.index}`}
                  id={`eventdate_${props.index}`}
                  showPopperArrow={false}
                  fixedHeight={true}
                  selected={startDate}
                  onChange={handleDateChange}
                  onChangeRaw={handleRawChange}
                  dateFormat={DATE_PICKER_FORMAT}
                  placeholderText={"mm/dd/yyyy"}
                  ref={inputRef}
                  className={`form-control date-picker-input ${
                    emptyStates?.eventDate || emptyStates?.eventDateInvalid
                      ? "is-invalid"
                      : EMPTY_STRING
                  }`}
                  minDate={getFutureDate(1)}
                  openToDate={startDate || getFutureDate(1)}
                  customInput={<input readOnly inputMode="none" />}
                />
                <div
                  onClick={() => inputRef.current.setFocus()} // opens calendar on icon click
                  className="input-group-icon"
                >
                  <DateCalenderSvg />
                </div>
                {emptyStates?.eventDate ? (
                  <div className="d-block invalid-feedback">
                    Date is required
                  </div>
                ) : (
                  emptyStates?.eventDateInvalid && (
                    <div className="d-block invalid-feedback">
                      This field is invalid
                    </div>
                  )
                )}
              </div>
            </div>
            <div className="col-md-3 pr-0">
              <label
                ref={(element) => {
                  if (element) {
                    if (
                      !Array.isArray(focusErrorFieldRef.current[props.event.id])
                    ) {
                      focusErrorFieldRef.current[props.event.id] = [];
                    }
                    focusErrorFieldRef.current[props.event.id].push(element);
                  }
                }}
                htmlFor={fieldName.EVENT_START_TIME}
                className="form-label d-inline-flex label-content-500"
              >
                <span>{t("event_time")}:</span>
              </label>
              <div className="position-relative date-time-picker-wrapper mb-3">
                <DatePicker
                  selected={
                    props.event.eventStartTime
                      ? new Date(props.event.eventStartTime)
                      : null
                  }
                  onChange={(time) => {
                    const selectedDate = props.event.eventDate
                      ? new Date(props.event.eventDate)
                      : new Date();

                    const combinedDateTime = combineDateAndTime(
                      selectedDate,
                      time
                    );

                    updateEventField({
                      [fieldName.EVENT_START_TIME]: combinedDateTime,
                      [fieldName.SHUTTERB_START_TIME]: combinedDateTime,
                    });

                    props.setEmptyStates({
                      eventStartTime: !time,
                      sbStartTime: !time,
                    });
                  }}
                  showPopperArrow={false}
                  fixedHeight={true}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  timeFormat="h:mm aa" // Use "h:mm aa" for 12-hour format
                  dateFormat="h:mm aa" // Use "h:mm aa" for 12-hour format
                  className={`input-height-time`}
                  popperClassName="custom-datepicker-popper"
                  customInput={
                    <div className="input-group">
                      <input
                        readOnly
                        inputMode="none"
                        style={customInputStyle}
                        name={`event[${props.index}].eventStartTime`}
                        required
                        className={`form-control date-picker-input ${
                          emptyStates?.eventStartTime
                            ? "is-invalid"
                            : EMPTY_STRING
                        }`}
                        value={
                          props.event.eventStartTime
                            ? new Date(
                                props.event.eventStartTime
                              ).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                              })
                            : EMPTY_STRING
                        }
                      />
                      <div className="input-group-append">
                        <span
                          className={`input-group-text clock-icon ${
                            emptyStates?.eventStartTime
                              ? "is-invalid-time"
                              : EMPTY_STRING
                          }`}
                        >
                          <ClockSvg />
                        </span>
                      </div>
                    </div>
                  }
                  // Additional props can be added as per your requirement
                />
                {emptyStates?.eventStartTime && (
                  <div className="is-time-field-empty">
                    {messages.REQUIRED_FIELD}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 pr-0 mb-3">
              <label
                ref={(element) => {
                  if (element) {
                    if (
                      !Array.isArray(focusErrorFieldRef.current[props.event.id])
                    ) {
                      focusErrorFieldRef.current[props.event.id] = [];
                    }
                    focusErrorFieldRef.current[props.event.id].push(element);
                  }
                }}
                htmlFor={fieldName.EVENT_LOCATION}
                className="form-label d-inline-flex label-content-500"
              >
                <span>{t("event_location")}:</span>

                <div className="label-icon-con">
                  <CustomTooltip
                    id={`tooltip1`}
                    title={t("event_location_message")}
                    arrow
                    placement="right"
                  >
                    <span tabIndex="0">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="info-icon"
                      />
                    </span>
                  </CustomTooltip>
                </div>
              </label>
              <div className="places-autocomplete-container">
                <PlacesAutocomplete
                  value={props.event?.eventLocation?.currentLocation || address}
                  onChange={(e) => {
                    setAddress(e);
                  }}
                  onSelect={handleSelect}
                >
                  {({
                    getInputProps,
                    suggestions,
                    getSuggestionItemProps,
                    loading,
                  }) => {
                    if (suggestions.length > 0) {
                      setAutocompleteSuggestions(suggestions);
                    }

                    return (
                      <div className="icon-field-con">
                        <div className="position-relative">
                          <AvField
                            name={`event[${props.index}].eventLocation.currentLocation`}
                            type="address"
                            className="field-with-icon"
                            value={
                              props.event?.eventLocation?.currentLocation ||
                              address
                            }
                            validate={{
                              required: {
                                value: true,
                                errorMessage: t("enter_a_location"),
                              },
                              custom: validatePostalCodes,
                            }}
                            errorMessage={t("enter_a_location")}
                            placeholder={t("enter_a_location")}
                            {...getInputProps()}
                          />
                          <div className="input-group-icon">
                            <MapMarkerSvg />
                          </div>
                        </div>

                        <div className="address-suggestions-con px-2 suggestion-zIndex">
                          {loading ? <div>{t("loading")}</div> : null}
                          {suggestions.map((suggestion, index) => {
                            const style = {
                              backgroundColor: suggestion.active
                                ? "#eaeaea"
                                : "#fff",
                            };
                            return (
                              <>
                                <div
                                  className="address-suggestions"
                                  key={index}
                                  {...getSuggestionItemProps(suggestion, {
                                    style,
                                  })}
                                >
                                  <MapMarker className="map-icon" />
                                  {suggestion.description}
                                </div>
                                <hr className="p-0 m-0" />
                              </>
                            );
                          })}
                          {suggestions.length > 0 && (
                            <div className="google-trademark p-0 m-0">
                              powered by
                              <div className="google-svg-con">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  height="250"
                                  width="400"
                                  viewBox="-40.446 -22.19 350.532 133.14"
                                >
                                  <path
                                    d="M115.39 46.71c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.86 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"
                                    fill="#EA4335"
                                  />
                                  <path
                                    d="M163.39 46.71c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"
                                    fill="#FBBC05"
                                  />
                                  <path
                                    d="M209.39 25.87v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"
                                    fill="#4285F4"
                                  />
                                  <path
                                    d="M224.64 2.53v65h-9.5v-65z"
                                    fill="#34A853"
                                  />
                                  <path
                                    d="M261.66 54.01l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"
                                    fill="#EA4335"
                                  />
                                  <path
                                    d="M34.93 40.94v-9.41h31.71c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C15.96 68.88 0 53.42 0 34.44 0 15.46 15.96 0 34.94 0c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65z"
                                    fill="#4285F4"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </PlacesAutocomplete>
              </div>
            </div>
            <div className="col-md-6 pr-0">
              <label
                ref={(element) => {
                  if (element) {
                    if (
                      !Array.isArray(focusErrorFieldRef.current[props.event.id])
                    ) {
                      focusErrorFieldRef.current[props.event.id] = [];
                    }
                    focusErrorFieldRef.current[props.event.id].push(element);
                  }
                }}
                htmlFor={fieldName.APPARTMENT}
                className="form-label label-content-500"
              >
                {t("appartment_unit_or_suite_no")}
              </label>
              <AvField
                name={`event[${props.index}].apartment`}
                type="text"
                maxLength="90"
                value={props.event.apartment}
                onChange={(event, value) => {
                  updateEventField({ [fieldName.APPARTMENT]: value });
                }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-md-4 pr-0">
              <label
                ref={(element) => {
                  if (element) {
                    if (
                      !Array.isArray(focusErrorFieldRef.current[props.event.id])
                    ) {
                      focusErrorFieldRef.current[props.event.id] = [];
                    }
                    focusErrorFieldRef.current[props.event.id].push(element);
                  }
                }}
                htmlFor={fieldName.SHUTTERB_START_TIME}
                className="form-label d-inline-flex label-content-500"
              >
                <span>{t("shutterb_start_time")}:</span>
                <div className="pl-1">
                  <CustomTooltip
                    id={`tooltip2`}
                    title={t("shutterb_start_time_message")}
                    arrow
                    placement="right"
                  >
                    <span tabIndex="0">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="info-icon"
                      />
                    </span>
                  </CustomTooltip>
                </div>
              </label>
              <div className="position-relative mb-3">
                <DatePicker
                  selected={
                    props.event.sbStartTime
                      ? new Date(props.event.sbStartTime)
                      : null
                  }
                  onChange={(time) => {
                    const selectedDate = props.event.eventDate
                      ? new Date(props.event.eventDate)
                      : new Date();

                    const combinedDateTime = combineDateAndTime(
                      selectedDate,
                      time
                    );

                    updateEventField({
                      [fieldName.SHUTTERB_START_TIME]: combinedDateTime,
                    });
                    props.setEmptyStates({
                      sbStartTime: !time,
                    });
                  }}
                  showPopperArrow={false}
                  fixedHeight={true}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  timeFormat="h:mm aa" // Use "h:mm aa" for 12-hour format
                  dateFormat="h:mm aa" // Use "h:mm aa" for 12-hour format
                  className={`input-height-time`}
                  popperClassName="custom-datepicker-popper"
                  customInput={
                    <div className="input-group">
                      <input
                        readOnly
                        inputMode="none"
                        style={customInputStyle}
                        required
                        className={`form-control date-picker-input ${
                          emptyStates?.sbStartTime ? "is-invalid" : EMPTY_STRING
                        }`}
                        value={
                          props.event.sbStartTime
                            ? new Date(
                                props.event.sbStartTime
                              ).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                              })
                            : ""
                        }
                      />
                      <div className="input-group-append">
                        <span
                          className={`input-group-text clock-icon ${
                            emptyStates?.sbStartTime
                              ? "is-invalid-time"
                              : EMPTY_STRING
                          }`}
                        >
                          <ClockSvg />
                        </span>
                      </div>
                    </div>
                  }
                  // Additional props can be added as per your requirement
                />
                {emptyStates?.sbStartTime && (
                  <div className="is-time-field-empty">
                    {messages.REQUIRED_FIELD}
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-3 col-md-4 pr-0">
              <label
                ref={(element) => {
                  if (element) {
                    if (
                      !Array.isArray(focusErrorFieldRef.current[props.event.id])
                    ) {
                      focusErrorFieldRef.current[props.event.id] = [];
                    }
                    focusErrorFieldRef.current[props.event.id].push(element);
                  }
                }}
                htmlFor={fieldName.DURATION}
                className="form-label label-content-500"
              >
                {t("event_duration")}:
              </label>
              <InputGroupWrapper>
                <AvField
                  type="select"
                  name={`event[${props.index}].duration`}
                  className={"pr-5 pr-md-2 select-dropdown"}
                  value={props.event.duration}
                  onChange={(event, value) => {
                    updateEventField({
                      [fieldName.DURATION]: parseFloat(value),
                    });
                  }}
                  validate={{
                    required: {
                      value: true,
                      errorMessage: t("please_enter_a_duration"),
                    },
                  }}
                >
                  <option selected disabled value="">
                    select duration
                  </option>
                  {durationOptions.map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </AvField>
                <ChevronDownSvg />
              </InputGroupWrapper>
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  formState: state.eventBooking.formState,
  isEditEvent: state.eventBooking.isEditEvent,
  postalCodesList: state.configurations.postalCodesList,
  isAuthenticated: state.authentication.isAuthenticated,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  loginAsAdmin: (isAuthenticated) =>
    dispatch(eventBookingActions.loginAsAdmin(isAuthenticated)),
  setEventAlreadyExist: (data) =>
    dispatch(eventBookingActions.setEventAlreadyExist(data)),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(EventSetup));
